/**
 * HomeDashboard — o Dashboard do Consórcio, no desenho bento (redesign 2026-08).
 *
 * Até 30/09/2026 era o Início: abria com a saudação e o resumo do agente. Os
 * dois foram para o Feed, que virou a tela de entrada do produto, e aqui
 * ficaram só os números (rota PRODUCT_MODULES.consortium.dashboard).
 *
 * A página é uma composição de cards de ORDEM FIXA: posição estável se
 * aprende, ordem que muda todo dia se procura. Tudo é determinístico e lê do
 * banco ao vivo.
 *
 * `?foco=<id>` rola até o card com esse id assim que ele monta — é como o
 * "Fila de hoje" do Feed chega na fila.
 *
 * Visibilidade por papel (via useRoleDataScope):
 *  - Super admin: vê tudo, pode "ver como" um vendedor (aí a página finge ser
 *    a dele: sai ranking, comissões, quebras de equipe e o texto do agente).
 *  - Gerente: vê o próprio + a equipe; ganha ranking e vocabulário de "meta".
 *  - Vendedor: vê só o seu; cards de gestão nem montam, as linhas da grade se
 *    refazem — card vazio não fica guardando lugar.
 *
 * O dado mora em bento/useDadosInicio; os cards com fonte própria (fila,
 * persistência, ranking, assembleias) buscam sozinhos, como os blocos antigos
 * faziam — a RLS é quem recorta o escopo deles.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PageSpinner } from '@/components/ui/page-spinner';
import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { useRoleDataScope } from '../commons/useRoleDataScope';
import { CabecalhoInicio } from './bento/CabecalhoInicio';
import { CardFilaDeHoje } from './bento/CardFilaDeHoje';
import { CardProducaoMes } from './bento/CardProducaoMes';
import { CardTicketMedio } from './bento/cardsIndicadores';
import { CardInadimplencia } from './bento/CardInadimplencia';
import { CardCarteiraAtiva } from './bento/CardCarteiraAtiva';
import { CardEmAndamento } from './bento/CardEmAndamento';
import { CardEquipeNoMes } from './bento/CardEquipeNoMes';
import { FaixaAssembleias } from './bento/FaixaAssembleias';
import { janelaDoPeriodo, somaDoPeriodo, type Periodo } from './bento/periodo';
import { useAlvoDoPeriodo } from './bento/useAlvoDoPeriodo';
import { useDadosInicio } from './bento/useDadosInicio';
import { useFilaDoDia } from './bento/useFilaDoDia';
import { useObjetivoDoMes } from './bento/useObjetivoDoMes';
import { ValoresOcultosProvider } from '../valores-ocultos/ValoresOcultos';

/**
 * Premium North (19/10/2026): os quatro cartões pequenos viram UMA faixa — um
 * cartão só, com filete dourado no topo e divisórias de 1px (o fundo da grade
 * aparece entre as células). Os cartões são os mesmos de antes; só perdem a
 * moldura própria.
 */
const FAIXA_INDICADORES =
  'grid grid-cols-1 gap-px overflow-hidden bg-border cartao border-t-ouro ' +
  '[&>div]:!rounded-none [&>div]:!border-0 [&>div]:!shadow-none';

const toDigits = (value: string | null | undefined) =>
  typeof value === 'string' ? value.replace(/\D/g, '') : '';

// O olhinho dos valores (09/10/2026) envolve a página inteira: cada card,
// gráfico e diálogo lê dele. O botão fica no CabecalhoInicio.
export const HomeDashboard = () => (
  <ValoresOcultosProvider>
    <DashboardConteudo />
  </ValoresOcultosProvider>
);

const DashboardConteudo = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const foco = searchParams.get('foco');
  const {
    isLoading: scopeLoading,
    isSuperAdmin,
    isManager,
    currentCrmUserId,
    currentCpf,
    scopedCrmUserIds,
    scopedCpfs,
  } = useRoleDataScope();

  // Super admin escolhe QUEM está olhando: nenhum (a casa toda), um
  // especialista, vários, ou o time de um gerente de uma vez.
  const [viewAsCrmUserIds, setViewAsCrmUserIds] = useState<number[]>([]);
  const [vendedores, setVendedores] = useState<{ id: number; name: string; cpf: string; gerenteId: number | null }[]>([]);
  const cpfById = useMemo(() => new Map(vendedores.map((v) => [v.id, v.cpf])), [vendedores]);

  useEffect(() => {
    if (scopeLoading || !isSuperAdmin) return;
    let active = true;
    (async () => {
      // manager_id vem junto para o seletor poder oferecer "o time do fulano".
      const { data: rows } = await supabase.from('users').select('id, name, cpf_cnpj, manager_id').order('name');
      if (!active) return;
      setVendedores(
        ((rows ?? []) as Array<{ id: number; name: string | null; cpf_cnpj: string | null; manager_id: number | null }>)
          .filter((u) => u.name)
          .map((u) => ({
            id: u.id,
            name: u.name as string,
            cpf: toDigits(u.cpf_cnpj),
            gerenteId: u.manager_id ?? null,
          })),
      );
    })();
    return () => {
      active = false;
    };
  }, [scopeLoading, isSuperAdmin]);

  const { data, loading } = useDadosInicio({
    scopeLoading,
    isSuperAdmin,
    currentCrmUserId,
    currentCpf,
    scopedCrmUserIds,
    scopedCpfs,
    viewAsCrmUserIds,
    cpfById,
  });
  // Precisa vir ANTES do hook da fila: é ele que diz de quem é a fila.
  const vendoComo = isSuperAdmin && viewAsCrmUserIds.length > 0;
  const effectiveIds: number[] | null = vendoComo
    ? viewAsCrmUserIds
    : isSuperAdmin
      ? null
      : scopedCrmUserIds;

  // Objetivo pessoal e os cartões que dizem "de quem é" só fazem sentido com
  // UMA pessoa em cena; com várias, a tela volta a falar no plural.
  const viewAsUnico = viewAsCrmUserIds.length === 1 ? viewAsCrmUserIds[0] : null;

  const { itens: fila, nomesVendedores } = useFilaDoDia(effectiveIds);
  const objetivo = useObjetivoDoMes({
    scopeLoading,
    isSuperAdmin,
    isManager,
    currentCrmUserId,
    viewAsCrmUserId: viewAsUnico,
    realizadoEscopo: data.vendasMesValor,
  });

  // ── O PERÍODO DOS CARDS DE PRODUÇÃO ───────────────────────────────────────
  // Mora aqui, e não dentro dos cards, porque "Produção" e "Ticket médio"
  // mostram o MESMO controle: clicar num deles troca os dois na hora. Nasce em
  // 'mes' e NÃO é lembrado entre sessões — o Início é a foto de hoje, e abrir
  // a tela no semestre porque alguém clicou ali semana passada esconde o mês
  // que está correndo.
  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const janela = useMemo(() => janelaDoPeriodo(periodo), [periodo]);
  const realizadoPeriodo = useMemo(
    () => somaDoPeriodo(data.vendasPorMes, janela).valor,
    [data.vendasPorMes, janela],
  );
  // Mês continua com o dono de sempre (a view do desafio/meta do mês, que os
  // outros cards também usam); semestre e ano somam as metas mensais.
  const alvoDoPeriodo = useAlvoDoPeriodo({
    scopeLoading,
    isSuperAdmin,
    isManager,
    currentCrmUserId,
    viewAsCrmUserId: viewAsUnico,
    janela,
    realizadoPeriodo,
  });
  const alvoDoCard = periodo === 'mes' ? objetivo : alvoDoPeriodo;

  // "Ver como" ativo = a página finge ser o vendedor escolhido.
  const ehGestao = (isSuperAdmin || isManager) && !vendoComo;
  const escopoProprio = !ehGestao;

  // "X de usuários gerenciados" — só pra quem enxerga além do próprio.
  const managedNote = (total: number, own: number) =>
    ehGestao && total - own > 0 ? `${total - own} de usuários gerenciados` : undefined;

  // Chegou com ?foco=: rola até o card UMA vez, quando ele existir (a fila é
  // a última a chegar, por isso depende dela). Uma vez só porque a fila se
  // recarrega ao marcar um item, e rolar de novo arrancaria a pessoa do lugar.
  const rolouPara = useRef<string | null>(null);
  useEffect(() => {
    if (!foco || rolouPara.current === foco || loading || scopeLoading) return;
    const alvo = document.getElementById(foco);
    if (!alvo) return;
    rolouPara.current = foco;
    alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [foco, loading, scopeLoading, fila]);

  return (
    <div className="space-y-4">
      <CabecalhoInicio
        isSuperAdmin={isSuperAdmin}
        vendedores={vendedores}
        viewAsCrmUserIds={viewAsCrmUserIds}
        onViewAsChange={setViewAsCrmUserIds}
      />

      {loading || scopeLoading ? (
        <PageSpinner />
      ) : (
        <>
          {/* v3 (2026-08-25): o número de caixas do 1b é o TETO. O gráfico
              grande absorveu objetivo, ano e 6 meses (toggle); atrasos e
              pendentes viraram caixinhas clicáveis (as listas moram nas
              páginas); persistência entrou na Carteira ativa. Vendedor mantém
              assembleias; gestão troca por "Equipe no mês" (objetivo + ritmo
              + barras por vendedor + comissões). */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            {/* VENDEDOR: caixinhas em linha no topo, gráfico e fila lado a
                lado embaixo (a fila esticada na linha inteira ficou ruim —
                pedido de 2026-08-25). GESTÃO: gráfico alto à esquerda com as
                caixinhas empilhadas ao lado, fila + equipe embaixo. */}
            {!ehGestao && (
              <div className={`${FAIXA_INDICADORES} md:col-span-2 xl:col-span-4 sm:grid-cols-2 xl:grid-cols-4`}>
                <CardTicketMedio
                  data={data}
                  periodo={periodo}
                  janela={janela}
                  onPeriodoChange={setPeriodo}
                />
                <CardInadimplencia data={data} navigate={navigate} />
                <CardCarteiraAtiva
                  data={data}
                  isSuperAdmin={isSuperAdmin}
                  scopedCrmUserIds={scopedCrmUserIds}
                  viewAsCrmUserId={viewAsUnico}
                  navigate={navigate}
                />
                <CardEmAndamento data={data} managedNote={managedNote} navigate={navigate} />
              </div>
            )}

            <CardProducaoMes
              data={data}
              objetivo={alvoDoCard}
              escopoProprio={escopoProprio}
              periodo={periodo}
              janela={janela}
              onPeriodoChange={setPeriodo}
              navigate={navigate}
              alto={ehGestao}
            />

            {ehGestao && (
              <div className={`${FAIXA_INDICADORES} md:col-span-2 xl:row-span-2 sm:grid-cols-2`}>
                <CardTicketMedio
                  data={data}
                  periodo={periodo}
                  janela={janela}
                  onPeriodoChange={setPeriodo}
                />
                <CardInadimplencia data={data} navigate={navigate} />
                <CardCarteiraAtiva
                  data={data}
                  isSuperAdmin={isSuperAdmin}
                  scopedCrmUserIds={scopedCrmUserIds}
                  viewAsCrmUserId={viewAsUnico}
                  navigate={navigate}
                />
                <CardEmAndamento data={data} managedNote={managedNote} navigate={navigate} />
              </div>
            )}

            {fila && (
              <CardFilaDeHoje
                itens={fila}
                navigate={navigate}
                col={2}
                gestao={
                  ehGestao
                    ? { propriaId: currentCrmUserId, nomes: nomesVendedores }
                    : undefined
                }
              />
            )}
            {ehGestao && (
              <CardEquipeNoMes
                data={data}
                objetivo={objetivo}
                isSuperAdmin={isSuperAdmin}
                navigate={navigate}
              />
            )}

            {!ehGestao && <FaixaAssembleias effectiveIds={effectiveIds} navigate={navigate} />}
          </div>
        </>
      )}
    </div>
  );
};
